import { InvoiceItemCategoryDto, InvoiceItemCategoriesListDto } from "Api/Api";
import {
  PaginationParams,
  PrimaryDataTable,
  TableIDType,
} from "Components/Shared/PrimaryDataTable/PrimaryDataTable";
import * as React from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import { Resources, useResource } from "Translations/Resources";
import { DetailColumn } from "Components/Shared/PrimaryDataTable/DetailColumn";
import { Dictionary } from "lodash";
import { FilterProps } from "Components/Shared/PrimaryDataTable/PrimaryDataTableFilter";
import { OrderByProps } from "Components/Shared/PrimaryDataTable/PrimaryDataTableOrderBy";
import { useHasCurrentUserAccessRight } from "Hooks/useHasCurrentUserAccessRight";
import { AccessRightCodes } from "Components/Models/AccessRights";

type Props = {
  list: InvoiceItemCategoriesListDto;
  onPageChange: (page: number, rows: number) => void;
  isLoading: boolean;
  filter: Dictionary<FilterProps>;
  orderBy: OrderByProps<InvoiceItemCategoryDto>;
  paginationParams?: PaginationParams | null;
};

const StyledTableWrapper = styled.div`
  margin-top: ${props => props.theme.spacing(3)}px;
`;

export const InvoiceItemCategoriesTable: React.FunctionComponent<Props> = props => {
  const {
    list,
    onPageChange,
    isLoading,
    filter,
    orderBy,
    paginationParams,
  } = props;
  const history = useHistory();
  const { t } = useResource();

  const hasAccessRight = useHasCurrentUserAccessRight();
  const canEdit = hasAccessRight([
    AccessRightCodes.Core_InvoiceItemCategories_Edit,
  ]);

  const onRowClick = (item: InvoiceItemCategoryDto) => {
    history.push(
      `/${t(Resources.Routes.InvoiceItemCategories)}/${t(
        Resources.Routes.InvoiceItemCategoryDetail,
      )}/${item.invoiceItemCategoryCode}`,
    );
  };

  const getCell = (
    id: TableIDType<InvoiceItemCategoryDto>,
    item: InvoiceItemCategoryDto,
  ) => {
    switch (id) {
      case "name":
        return <div>{item.name}</div>;
      case "isCode":
        return <div>{item.isCode}</div>;
      case "divisionName":
        return <div>{item.divisionName}</div>;
      case "detail":
        return (
          <DetailColumn
            url={`/${t(Resources.Routes.InvoiceItemCategories)}/${t(
              Resources.Routes.InvoiceItemCategoryDetail,
            )}/${item.invoiceItemCategoryCode}`}
          />
        );
      default:
        return <div>-</div>;
    }
  };

  return (
    <StyledTableWrapper>
      <PrimaryDataTable
        isLoading={isLoading}
        rows={
          list.invoiceItemCategories?.map(item => ({
            data: item,
            rowEntityID: "invoiceItemCategoryCode",
          })) ?? []
        }
        columns={[
          {
            id: "name",
            label: t(Resources.InvoiceItemCategories.List.Name),
            width: "40%",
            filter: filter["filterName"],
            orderBy: orderBy,
          },
          {
            id: "isCode",
            label: t(Resources.InvoiceItemCategories.List.ISCode),
            filter: filter["filterIsCode"],
            orderBy: orderBy,
          },
          {
            id: "divisionName",
            label: t(Resources.InvoiceItemCategories.List.Division),
            filter: filter["filterDivisionName"],
            orderBy: orderBy,
          },
          {
            id: "detail",
            isHidden: !canEdit,
          },
        ]}
        getCell={getCell}
        onPageChange={onPageChange}
        totalCount={list.count}
        onRowClick={canEdit ? onRowClick : undefined}
        getHeaderCell={column => <div>{column.label}</div>}
        paginationParams={paginationParams}
      />
    </StyledTableWrapper>
  );
};
